import React, { useState, useEffect, useCallback } from 'react';
import { getBots, toggleBot, generateInvoice } from '../../api/adminApi';
import LoadingData from '../../components/LoadingData';
import { useToast } from '../../context/ToastContext';
import { theme } from '../../adminTheme';

export default function UsersBotsPage() {
  const toast = useToast();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedUser, setExpandedUser] = useState(null);
  const [invoiceModal, setInvoiceModal] = useState(null);
  const [confirmToggle, setConfirmToggle] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await getBots(true);
      setData(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleToggle = async () => {
    if (!confirmToggle) return;
    try {
      await toggleBot(confirmToggle.id);
      setConfirmToggle(null);
      fetchData();
    } catch {
      toast.addToast('Error al cambiar estado del bot', 'error');
    }
  };

  const handleGenerateInvoice = async (e) => {
    e.preventDefault();
    const { botId, userId, year, month, feePercentage } = invoiceModal;
    try {
      await generateInvoice(botId, userId, parseInt(year), parseInt(month), parseFloat(feePercentage));
      toast.addToast('Factura generada exitosamente', 'success');
      setInvoiceModal(null);
    } catch (err) {
      toast.addToast(err.response?.data?.detail || 'Error al generar factura', 'error');
    }
  };

  if (loading) return <LoadingData />;

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Usuarios y Bots</h1>
      {Object.keys(data).length === 0 && (
        <p style={{ color: theme.textMuted }}>No hay usuarios con bots</p>
      )}
      {Object.entries(data).map(([userId, group]) => (
        <div key={userId} style={styles.userCard}>
          <div style={styles.userHeader} onClick={() => setExpandedUser(expandedUser === userId ? null : userId)}>
            <div>
              <strong style={{ color: theme.text }}>{group.user.username}</strong>
              <span style={styles.userEmail}> {group.user.email}</span>
              <span style={styles.roleBadge}>{group.user.role}</span>
            </div>
            <div style={styles.botCount}>
              <span style={{ color: group.active_bots > 0 ? theme.success : theme.textMuted }}>
                {group.active_bots}/{group.total_bots}
              </span> bots activos
              <span style={styles.arrow}>{expandedUser === userId ? '▲' : '▼'}</span>
            </div>
          </div>
          {expandedUser === userId && (
            <div style={styles.botList}>
              {group.bots.length === 0 && <p style={{ color: theme.textMuted, fontStyle: 'italic' }}>Este usuario no tiene bots</p>}
              {group.bots.map((bot, idx) => (
                <div key={bot.id} style={{ ...styles.botRow, backgroundColor: idx % 2 === 0 ? theme.tableRow : theme.tableRowAlt }}>
                  <div style={styles.botInfo}>
                    <strong style={{ color: theme.text }}>{bot.pair} ({bot.candle_period})</strong>
                    <span style={styles.botDetail}> {bot.exchange_name}</span>
                    <span style={styles.botDetail}> {bot.bot_strategy}</span>
                    <span style={{
                      ...styles.statusBadge,
                      backgroundColor: bot.is_active ? theme.statusOn.bg : theme.statusOff.bg,
                      color: bot.is_active ? theme.statusOn.color : theme.statusOff.color,
                    }}>
                      {bot.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                  <div style={styles.botActions}>
                    <span style={{ ...styles.botProfit, color: (bot.profit || 0) >= 0 ? theme.success : theme.error }}>
                      ${(bot.profit || 0).toFixed(2)}
                    </span>
                    <button onClick={() => setConfirmToggle({ id: bot.id, name: `${bot.pair} (${bot.candle_period})`, action: bot.is_active ? 'Apagar' : 'Encender' })} style={styles.actionBtn}>
                      {bot.is_active ? 'Apagar' : 'Encender'}
                    </button>
                    <button onClick={() => setInvoiceModal({ botId: bot.id, userId, year: new Date().getFullYear(), month: new Date().getMonth() + 1, feePercentage: 10 })} style={styles.actionBtn}>
                      Factura
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {confirmToggle && (
        <div style={styles.modalOverlay} onClick={() => setConfirmToggle(null)}>
          <div style={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: theme.text, marginTop: 0 }}>Confirmar Acción</h3>
            <p style={{ color: theme.textMuted }}>
              ¿Estás seguro de que deseas <strong style={{ color: theme.text }}>{confirmToggle.action.toLowerCase()}</strong> el bot <strong style={{ color: theme.text }}>{confirmToggle.name}</strong>?
            </p>
            <div style={styles.modalActions}>
              <button onClick={handleToggle} style={styles.submitBtn}>Sí, {confirmToggle.action}</button>
              <button onClick={() => setConfirmToggle(null)} style={styles.cancelBtn}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {invoiceModal && (
        <div style={styles.modalOverlay} onClick={() => setInvoiceModal(null)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ color: theme.text, marginTop: 0 }}>Generar Factura</h2>
            <form onSubmit={handleGenerateInvoice}>
              <div style={styles.field}>
                <label style={styles.label}>Año</label>
                <input type="number" value={invoiceModal.year} onChange={(e) => setInvoiceModal({...invoiceModal, year: e.target.value})} required style={styles.input} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Mes (1-12)</label>
                <input type="number" min="1" max="12" value={invoiceModal.month} onChange={(e) => setInvoiceModal({...invoiceModal, month: e.target.value})} required style={styles.input} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Fee %</label>
                <input type="number" step="0.1" value={invoiceModal.feePercentage} onChange={(e) => setInvoiceModal({...invoiceModal, feePercentage: e.target.value})} required style={styles.input} />
              </div>
              <div style={styles.modalActions}>
                <button type="submit" style={styles.submitBtn}>Generar</button>
                <button type="button" onClick={() => setInvoiceModal(null)} style={styles.cancelBtn}>Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: '24px', minHeight: '100vh' },
  title: { margin: '0 0 24px 0', fontSize: '1.5rem', color: theme.text },
  userCard: { backgroundColor: theme.card, borderRadius: '8px', marginBottom: '12px', border: `1px solid ${theme.border}`, overflow: 'hidden' },
  userHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', cursor: 'pointer', borderBottom: `1px solid ${theme.border}` },
  userEmail: { color: theme.textMuted, fontSize: '0.9rem', marginLeft: 8 },
  roleBadge: { marginLeft: 8, padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', backgroundColor: theme.accent, color: '#fff', textTransform: 'uppercase' },
  botCount: { color: theme.textMuted, fontSize: '0.9rem' },
  arrow: { marginLeft: 8, fontSize: '0.8rem' },
  botList: { padding: '4px 20px' },
  botRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderRadius: '4px', margin: '4px 0' },
  botInfo: { display: 'flex', alignItems: 'center', gap: 8 },
  botDetail: { color: theme.textMuted, fontSize: '0.85rem' },
  statusBadge: { padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 500 },
  botActions: { display: 'flex', alignItems: 'center', gap: 8 },
  botProfit: { fontSize: '0.85rem', fontWeight: 600 },
  actionBtn: { padding: '6px 12px', border: `1px solid ${theme.border}`, borderRadius: '6px', backgroundColor: theme.cardHover, color: theme.text, cursor: 'pointer', fontSize: '0.85rem' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { backgroundColor: theme.card, padding: '24px', borderRadius: '12px', border: `1px solid ${theme.border}`, width: '90%', maxWidth: '420px' },
  confirmModal: { backgroundColor: theme.card, padding: '24px', borderRadius: '12px', border: `1px solid ${theme.border}`, width: '90%', maxWidth: '380px' },
  field: { marginBottom: '12px' },
  label: { color: theme.textMuted, display: 'block', marginBottom: 4, fontSize: '0.9rem' },
  input: { width: '100%', padding: '8px 12px', border: `1px solid ${theme.inputBorder}`, borderRadius: '6px', fontSize: '1rem', backgroundColor: theme.input, color: theme.text, boxSizing: 'border-box', marginTop: 4 },
  modalActions: { display: 'flex', gap: 8, marginTop: 16 },
  submitBtn: { flex: 1, padding: '10px', backgroundColor: theme.accent, color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' },
  cancelBtn: { flex: 1, padding: '10px', backgroundColor: 'transparent', border: `1px solid ${theme.border}`, borderRadius: '6px', cursor: 'pointer', color: theme.text },
};
